import React, { useEffect, useRef } from 'react';

const SheetsPage = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.animation = 'fadeInUp 0.8s ease-out forwards';
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll('.animate-on-scroll');
    animatedElements.forEach(el => {
      el.style.opacity = '0';
      el.style.transform = 'translateY(30px)';
      observerRef.current.observe(el);
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const specifications = [
    {
      category: "Alloy Grades",
      items: ["1100 Series", "1050 Series", "1070 Series", "3003 Series", "5052 Series"],
      icon: "fas fa-layer-group"
    },
    {
      category: "Temper Conditions",
      items: ["O (Annealed)", "H14 (Half Hard)", "H18 (Full Hard)", "H22", "H24"],
      icon: "fas fa-temperature-high"
    },
    {
      category: "Thickness Range",
      items: ["0.2mm - 6.0mm", "Custom thicknesses available", "Precision tolerance ±0.05mm"],
      icon: "fas fa-ruler"
    },
    {
      category: "Width Range",  
      items: ["200mm - 2000mm", "Standard widths: 1000mm, 1250mm", "Custom widths on request"],
      icon: "fas fa-arrows-alt-h"
    }
  ];

  const applications = [
    {
      title: "Automotive Industry",
      description: "Body panels, heat exchangers, fuel tanks",
      icon: "fas fa-car",
      image: "/images/automotive-aluminum-sheets.jpg"
    },
    {
      title: "Construction", 
      description: "Roofing, cladding, architectural elements",
      icon: "fas fa-building",
      image: "/images/construction-aluminum-sheets.jpg"
    },
    {
      title: "Electronics",
      description: "Heat sinks, enclosures, circuit boards",
      icon: "fas fa-microchip", 
      image: "/images/electronics-aluminum-sheets.jpg"
    },
    {
      title: "Packaging",
      description: "Food containers, beverage cans, foil products",
      icon: "fas fa-box",
      image: "/images/packaging-aluminum-sheets.jpg"
    }
  ];

  return (
    <div className="sheets-page">
      <style jsx>{`
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .sheets-page {
          font-family: 'Inter', 'Poppins', sans-serif;
          line-height: 1.6;
          color: #1E2329;
          overflow-x: hidden;
        }

        /* Hero Section */
        .hero {
          background: linear-gradient(135deg, #5A1FB3 0%, #1E2329 100%);
          padding: 120px 0 80px;
          position: relative;
          overflow: hidden;
        }

        .hero::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1000 100" fill="%23ffffff" opacity="0.05"><polygon points="0,0 1000,0 1000,100 0,80"/></svg>');
          background-size: cover;
        }

        .hero-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
          text-align: center;
          position: relative;
          z-index: 2;
        }

        .hero h1 {
          font-size: 3.5rem;
          font-weight: 800;
          color: white;
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .hero-subtitle {
          font-size: 1.3rem;
          color: rgba(255, 255, 255, 0.9);
          max-width: 800px;
          margin: 0 auto 2rem;
          font-weight: 400;
        }

        .hero-icon {
          width: 80px;
          height: 80px;
          background: #7B3FE4;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 2rem;
          box-shadow: 0 10px 30px rgba(123, 63, 228, 0.3);
        }

        .hero-icon i {
          font-size: 2rem;
          color: white;
        }

        /* Container */
        .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 2rem;
        }

        /* Production Showcase */
        .production-showcase {
          padding: 100px 0;
          background: linear-gradient(135deg, #F6F7F9 0%, #EDE7FF 100%);
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-header h2 {
          font-size: 2.8rem;
          font-weight: 700;
          background: linear-gradient(135deg, #7B3FE4 0%, #5A1FB3 100%);
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin-bottom: 1rem;
        }

        .section-header p {
          font-size: 1.2rem;
          color: #4A5260;
          max-width: 600px;
          margin: 0 auto;
        }

        .production-image-container {
          position: relative;
          max-width: 900px;
          margin: 0 auto;
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.12);
          transition: all 0.5s ease;
        }

        .production-image-container:hover {
          transform: translateY(-10px);
          box-shadow: 0 40px 100px rgba(0, 0, 0, 0.18);
        }

        .image-placeholder {
          width: 100%;
          height: 500px;
          background: #F7FAFC;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 3px dashed #7B3FE4;
          transition: all 0.3s ease;
        }

        .production-image-container:hover .image-placeholder {
          background: rgba(123, 63, 228, 0.05);
          border-color: #9A60FF;
        }

        .placeholder-content {
          text-align: center;
          color: #7B3FE4;
        }

        .placeholder-content i {
          font-size: 4rem;
          margin-bottom: 1.5rem;
          display: block;
        }

        .placeholder-text {
          font-size: 1.4rem;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }

        .placeholder-desc {
          font-size: 1.1rem;
          opacity: 0.8;
          max-width: 400px;
        }

        .image-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
          color: white;
          padding: 2rem;
          transform: translateY(100%);
          transition: transform 0.4s ease;
        }

        .production-image-container:hover .image-overlay {
          transform: translateY(0);
        }

        .image-overlay h3 {
          font-size: 1.8rem;
          margin-bottom: 0.5rem;
          font-weight: 600;
        }

        .image-overlay p {
          font-size: 1.1rem;
          opacity: 0.9;
        }

        /* Specifications Section */
        .specifications-section {
          padding: 100px 0;
          background: white;
        }

        .specs-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2.5rem;
        }

        .spec-card {
          background: #F7FAFC;
          padding: 2.5rem;
          border-radius: 20px;
          text-align: center;
          transition: all 0.4s ease;
          border: 2px solid transparent;
          position: relative;
          overflow: hidden;
        }

        .spec-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 4px;
          background: linear-gradient(90deg, #7B3FE4, #14B87A);
          transition: all 0.3s ease;
        }

        .spec-card:hover {
          border-color: #7B3FE4;
          transform: translateY(-8px);
          box-shadow: 0 20px 50px rgba(123, 63, 228, 0.15);
        }

        .spec-card:hover::before {
          height: 6px;
        }

        .spec-icon {
          width: 80px;
          height: 80px;
          background: linear-gradient(135deg, #9A60FF, #7B3FE4);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          transition: all 0.3s ease;
        }

        .spec-card:hover .spec-icon {
          transform: scale(1.1);
        }

        .spec-icon i {
          font-size: 2rem;
          color: white;
        }

        .spec-card h3 {
          font-size: 1.5rem;
          font-weight: 600;
          color: #1E2329;
          margin-bottom: 1rem;
        }

        .spec-list {
          list-style: none;
        }

        .spec-list li {
          padding: 0.5rem 0;
          color: #4A5260;
          position: relative;
          padding-left: 1.5rem;
          font-weight: 500;
        }

        .spec-list li::before {
          content: '•';
          position: absolute;
          left: 0;
          color: #7B3FE4;
          font-weight: bold;
          font-size: 1.2rem;
        }

        /* Applications Section */
        .applications-section {
          padding: 100px 0;
          background: linear-gradient(135deg, #1E2329 0%, #4A5260 100%);
          color: white;
        }

        .applications-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
        }

        .application-card {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 20px;
          overflow: hidden;
          transition: all 0.4s ease;
          border: 1px solid rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(10px);
        }

        .application-card:hover {
          transform: translateY(-10px);
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(123, 63, 228, 0.5);
        }

        .application-image-placeholder {
          width: 100%;
          height: 200px;
          background: rgba(255, 255, 255, 0.1);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px dashed rgba(255, 255, 255, 0.3);
          transition: all 0.3s ease;
        }

        .application-card:hover .application-image-placeholder {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(123, 63, 228, 0.5);
          transform: scale(1.05);
        }

        .application-content {
          padding: 2rem;
        }

        .app-header {
          display: flex;
          align-items: center;
          margin-bottom: 1rem;
        }

        .app-icon {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #7B3FE4, #14B87A);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 1rem;
        }

        .app-icon i {
          font-size: 1.5rem;
          color: white;
        }

        .application-card h3 {
          font-size: 1.3rem;
          font-weight: 600;
        }

        .application-card p {
          font-size: 1rem;
          opacity: 0.9;
          line-height: 1.6;
        }

        /* Drone Gallery Section */
        .drone-gallery {
          padding: 100px 0;
          background: #F6F7F9;
        }

        .drone-images {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 2rem;
        }

        .drone-image-container {
          position: relative;
          border-radius: 15px;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
        }

        .drone-image-container:hover {
          transform: translateY(-10px);
          box-shadow: 0 30px 80px rgba(0, 0, 0, 0.15);
        }

        .drone-image-placeholder {
          width: 100%;
          height: 250px;
          background: #F7FAFC;
          display: flex;
          align-items: center;
          justify-content: center;
          border: 2px dashed #7B3FE4;
          transition: all 0.3s ease;
        }

        .drone-image-container:hover .drone-image-placeholder {
          background: rgba(123, 63, 228, 0.05);
          border-color: #9A60FF;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .hero h1 {
            font-size: 2.5rem;
          }

          .hero-subtitle {
            font-size: 1.1rem;
          }

          .section-header h2 {
            font-size: 2.2rem;
          }

          .container {
            padding: 0 1rem;
          }

          .specs-grid {
            grid-template-columns: 1fr;
          }

          .applications-grid {
            grid-template-columns: 1fr;
          }

          .drone-images {
            grid-template-columns: 1fr;
          }

          .production-image {
            height: 300px;
          }
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animate-fade-in {
          animation: fadeInUp 0.8s ease-out;
        }
      `}</style>

      {/* Add Font Awesome and Google Fonts */}
      <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet" />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-icon animate-fade-in">
            <i className="fas fa-layer-group"></i>
          </div>
          <h1 className="animate-fade-in">Aluminum Sheets & Coils</h1>
          <p className="hero-subtitle animate-fade-in">
            Precision-manufactured aluminum sheets and coils for diverse industrial applications with superior quality and consistency
          </p>
        </div>
      </section>


      {/* Specifications Section */}
      <section className="specifications-section">
        <div className="container">
          <div className="section-header">
            <h2>Technical Specifications</h2>
            <p>Comprehensive range of aluminum sheet specifications to meet diverse industrial requirements</p>
          </div>
          
          <div className="specs-grid">
            {specifications.map((spec, index) => (
              <div key={index} className="spec-card animate-on-scroll">
                <div className="spec-icon">
                  <i className={spec.icon}></i>
                </div>
                <h3>{spec.category}</h3>
                <ul className="spec-list">
                  {spec.items.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Applications Section */}


      {/* Facility Gallery Section */}

    </div>
  );
};

export default SheetsPage;